// Shared data layer for the report builder: API root resolution, range math and
// fetch helpers that mirror what the AI Suite dashboard views request.
import helpers from "../helper/global"
import CryptoJS from "crypto-js"

export function resolveApiRoot(companyId) {
    const decoded = CryptoJS.AES.decrypt(companyId, process.env.VUE_APP_ENCRYPTION_PW).toString(CryptoJS.enc.Utf8)
    return JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(decoded) == -1 ? process.env.VUE_APP_MONITORING_API_URL : process.env.VUE_APP_APIURL + "/monitoring-api"
}

function post(companyId, path, payload) {
    // explicit timeout: a single hung request (e.g. right after a backend restart)
    // must not block a report section forever — sections handle rejects gracefully
    return helpers.axiosPost(resolveApiRoot(companyId) + path, { company_id: companyId, ...payload }, 90000)
}

// Report platform scope: single platform from the filter, or all platforms tracked by the project.
export function getReportPlatformKeys(filters, project) {
    const p = filters?.platform
    if (p && p !== "all") return [p]
    const tracked = Array.isArray(project?.platforms) ? project.platforms.map((x) => (x || "").toLowerCase()).filter(Boolean) : []
    // Google AIO isn't stored in the platforms array — it's tracked via the
    // separate project.google flag, so add it explicitly when enabled.
    if (project?.google && !tracked.includes("google")) tracked.push("google")
    return tracked.filter((k) => k !== "openai")
}

// Current range + equal-length previous range (same math as AIProject.computeBrandMetricsRanges).
export function computeRanges(selectedDates) {
    const dates = Array.isArray(selectedDates) ? selectedDates.filter(Boolean) : []
    if (!dates.length) return null
    const sorted = [...dates].sort()
    const from = sorted[0]
    const to = sorted[sorted.length - 1] || sorted[0]
    const fromDate = new Date(`${from}T00:00:00.000Z`)
    const toDate = new Date(`${to}T00:00:00.000Z`)
    if (isNaN(fromDate) || isNaN(toDate)) return null
    const spanDays = Math.max(1, Math.round((toDate - fromDate) / 86400000) + 1)
    const prevTo = new Date(fromDate)
    prevTo.setUTCDate(prevTo.getUTCDate() - 1)
    const prevFrom = new Date(prevTo)
    prevFrom.setUTCDate(prevFrom.getUTCDate() - (spanDays - 1))
    return {
        current: { from, to },
        previous: { from: prevFrom.toISOString().slice(0, 10), to: prevTo.toISOString().slice(0, 10) }
    }
}

export async function fetchBrandsAll(companyId, projectId) {
    const res = await post(companyId, "/ai-suite/brands/all/frontend/get", { project_id: projectId })
    return Array.isArray(res?.data?.brands) ? res.data.brands : []
}

export function brandsDataPayload(brands) {
    return (Array.isArray(brands) ? brands : [])
        .map((b) => ({
            canonicalName: b?.canonicalName || b?.brand_name || null,
            mentions: Array.isArray(b?.mentions) ? b.mentions : []
        }))
        .filter((b) => b.canonicalName || b.mentions.length)
}

// Avg brand metrics over the range incl. *Old fields for the previous range.
export async function fetchRangeAverages(companyId, { projectId, platform, ranges, brands, tags, tagsOperator, includeSources = false }) {
    const payload = {
        project_id: projectId,
        platform: platform || null,
        tags: tags && tags.length ? tags : null,
        tagsOperator: tagsOperator || "or",
        currentRange: ranges.current,
        previousRange: ranges.previous,
        includeSources
    }
    const brandsData = brandsDataPayload(brands)
    if (brandsData.length) payload.brandsData = brandsData
    const res = await post(companyId, "/ai-suite/brandvisibility/range-averages", payload)
    return Array.isArray(res?.data?.brands) ? res.data.brands : []
}

// Primary-brand KPI metrics (visibility/sentiment/rank/sources) current vs previous range.
export async function fetchMetricsRange(companyId, { projectId, platform, ranges, brand, tags, tagsOperator }) {
    const res = await post(companyId, "/ai-suite/brand/metrics-range", {
        project_id: projectId,
        platform: platform && platform !== "all" ? platform : null,
        brand,
        tags: tags && tags.length ? tags : null,
        tagsOperator: tagsOperator || "or",
        currentRange: ranges.current,
        previousRange: ranges.previous
    })
    return res?.data?.success ? res.data : null
}

export async function fetchPromptResponses(companyId, payload) {
    const res = await post(companyId, "/ai-suite/promptresponses/get", payload)
    return Array.isArray(res?.data?.responses) ? res.data.responses : []
}

// One-day snapshot of responses mentioning a brand — used twice for new/lost mention deltas.
export async function fetchMentionsSnapshot(companyId, { projectId, brand, day, platform, tags, tagsOperator }) {
    const payload = {
        project_id: projectId,
        brand,
        skip: 0,
        limit: 5000,
        sort: { importDate: -1 },
        fromDate: day,
        toDate: day
    }
    if (platform && platform !== "all") payload.platform = platform
    if (tags && tags.length) {
        payload.tags = tags
        payload.tagsOperator = tagsOperator || "or"
    }
    const res = await post(companyId, "/ai-suite/promptresponses/by-brand", payload)
    return Array.isArray(res?.data?.responses) ? res.data.responses : []
}

export async function fetchSources(companyId, payload) {
    const res = await post(companyId, "/ai-suite/sources/get", payload)
    return res?.data || {}
}

export async function fetchSourceUrls(companyId, payload) {
    const res = await post(companyId, "/ai-suite/sources/urls/get", payload)
    return res?.data || {}
}

export async function fetchTagsList(companyId, { projectId, platform }) {
    const res = await post(companyId, "/ai-suite/tags/list", {
        project_id: projectId,
        platform: platform && platform !== "all" ? platform : null
    })
    const tags = Array.isArray(res?.data?.tags) ? res.data.tags : []
    return tags.map((t) => (typeof t === "string" ? t : t.tag || t.name)).filter(Boolean)
}

// Picks the project's primary brand row out of a range-averages result and derives
// the KPI cell values (same logic as AIProject.extractBrandMetricsFromRows).
export function extractBrandMetricsFromRows(rows, brand) {
    const safeRows = Array.isArray(rows) ? rows : []
    const targetName = (brand?.canonicalName || brand?.brand_name || "").toLowerCase()
    const extraMentions = Array.isArray(brand?.mentions) ? brand.mentions.map((m) => (m || "").toLowerCase()) : []
    const ranked = safeRows.filter((b) => b && typeof b.visibilityPercent === "number" && Number.isFinite(b.visibilityPercent)).sort((a, b) => (b.visibilityPercent || 0) - (a.visibilityPercent || 0))

    const matchBrand = (entry) => {
        if (!entry) return false
        const entryCanon = (entry.canonicalName || "").toLowerCase()
        if (entryCanon && (entryCanon === targetName || extraMentions.includes(entryCanon))) return true
        if (Array.isArray(entry.mentions)) {
            return entry.mentions.map((m) => (m || "").toLowerCase()).some((m) => m && (m === targetName || extraMentions.includes(m)))
        }
        return false
    }

    const found = safeRows.find(matchBrand)
    let relevanceRank = null
    if (found) {
        const idx = ranked.indexOf(found)
        relevanceRank = idx > -1 ? idx + 1 : null
    }

    return {
        visibility: typeof found?.visibilityPercent === "number" ? found.visibilityPercent : null,
        relevanceRank,
        sourceReferences: typeof found?.sourcesCount === "number" ? found.sourcesCount : found?.sources?.current?.totalResponses ?? found?.sources?.totalResponses ?? null,
        sentiment: typeof found?.sentimentAvg === "number" ? found.sentimentAvg : null
    }
}

export function isPrimaryBrand(row, project) {
    const mainName = (project?.brand?.brand_name || "").toLowerCase()
    if (!mainName) return false
    if ((row?.canonicalName || "").toLowerCase() === mainName) return true
    return Array.isArray(row?.mentions) && row.mentions.some((m) => (m || "").toLowerCase() === mainName)
}

// Run promise-returning tasks with limited concurrency (tag matrix fires tags × platforms calls).
export async function runLimited(tasks, limit = 4) {
    const results = new Array(tasks.length)
    let next = 0
    const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
        while (next < tasks.length) {
            const idx = next++
            results[idx] = await tasks[idx]()
        }
    })
    await Promise.all(workers)
    return results
}

// Earliest and latest processed day of one platform inside the selected range.
// Backends match importDate exactly, so each platform needs its own snapshot days.
export function platformDaysInRange(project, selectedDates, platformKey) {
    const field = `processed_dates_ai_tracking_${platformKey}`
    const arr = Array.isArray(project?.[field]) ? project[field] : []
    const dates = (selectedDates || []).filter(Boolean).sort()
    if (!dates.length) return null
    const from = dates[0]
    const to = dates[dates.length - 1] || from
    const days = arr
        .map((d) => {
            const dt = new Date(d)
            return isNaN(dt) ? null : dt.toISOString().slice(0, 10)
        })
        .filter((d) => d && d >= from && d <= to)
        .sort()
    if (!days.length) return null
    return { start: days[0], end: days[days.length - 1] }
}

// Exact processed timestamps of one platform inside the selected day range.
// The sources backend matches importDate EXACTLY ($in), so day-truncated strings
// ("YYYY-MM-DD" -> midnight UTC) never hit the stored full timestamps and return
// nothing. Pick the earliest run of the first selected day and the latest run of
// the last selected day — both are real entries from the processed_dates array,
// mirroring the main app's buildImportDatesByPlatform.
export function platformSnapshotDates(project, selectedDates, platformKey) {
    const field = `processed_dates_ai_tracking_${platformKey}`
    const arr = Array.isArray(project?.[field]) ? project[field] : []
    const dates = (selectedDates || []).filter(Boolean).sort()
    if (!dates.length || !arr.length) return null
    const from = dates[0]
    const to = dates[dates.length - 1] || from
    const buckets = {}
    arr.forEach((d) => {
        const dt = new Date(d)
        if (isNaN(dt)) return
        const day = dt.toISOString().slice(0, 10)
        if (day < from || day > to) return
        if (!buckets[day]) buckets[day] = { earliest: dt, latest: dt }
        else {
            if (dt < buckets[day].earliest) buckets[day].earliest = dt
            if (dt > buckets[day].latest) buckets[day].latest = dt
        }
    })
    const days = Object.keys(buckets).sort()
    if (!days.length) return null
    const startDay = days[0]
    const endDay = days[days.length - 1]
    if (startDay === endDay) {
        const ts = buckets[endDay].latest.toISOString()
        return { start: ts, end: ts }
    }
    return { start: buckets[startDay].earliest.toISOString(), end: buckets[endDay].latest.toISOString() }
}

// Every processed day within the selected range across the given platforms (deduped, sorted).
// The trend chart needs the full list of available days so it plots every point in the range,
// not just the range endpoints.
export function availableDatesInRange(project, selectedDates, platformKeys) {
    const dates = (selectedDates || []).filter(Boolean).sort()
    if (!dates.length) return []
    const from = dates[0]
    const to = dates[dates.length - 1] || from
    const keys = Array.isArray(platformKeys) && platformKeys.length ? platformKeys : []
    const out = new Set()
    keys.forEach((k) => {
        const arr = project?.[`processed_dates_ai_tracking_${k}`]
        if (!Array.isArray(arr)) return
        arr.forEach((d) => {
            const dt = new Date(d)
            if (isNaN(dt)) return
            const iso = dt.toISOString().slice(0, 10)
            if (iso >= from && iso <= to) out.add(iso)
        })
    })
    return [...out].sort()
}

export function faviconUrl(domain) {
    if (!domain) return ""
    return `https://s2.googleusercontent.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=32`
}

// Section registry: groups, labels and the AI Suite view each section links to.
// `label` is the short name used in the builder's section list; `title` + `subtitle`
// are the reader-facing headings rendered on the report slides. English throughout.
export const SECTION_REGISTRY = [
    {
        type: "kpi_cards",
        group: "Overview",
        label: "Brand KPI Cards",
        title: "Brand Performance at a Glance",
        subtitle: "Visibility, sentiment, relevance rank and source references — with change vs. the previous period",
        icon: "mdi-view-grid-outline",
        route: ""
    },
    {
        type: "visibility_trend",
        group: "Overview",
        label: "Visibility Trend",
        title: "Visibility Over Time",
        subtitle: "Share of AI answers that mention each brand across the selected period",
        icon: "mdi-chart-line",
        route: "",
        options: [{ key: "per_platform", label: "One chart per platform" }],
        // builder shows a competitor picker; selection lands in options.brands
        brandPicker: true
    },
    {
        type: "top_brands_per_platform",
        group: "Brands",
        label: "Top Brands per Platform",
        title: "Top Brands per Platform",
        subtitle: "The five most visible brands in AI answers on each platform",
        icon: "mdi-podium",
        route: "Brands"
    },
    {
        type: "brand_table",
        group: "Brands",
        label: "Brand Comparison Table",
        title: "Brand Comparison",
        subtitle: "Rank, visibility, sentiment and mentions per brand — with change vs. the previous period",
        icon: "mdi-table",
        route: "Brands"
    },
    {
        type: "tag_performance",
        group: "Brands",
        label: "Topic Performance Matrix",
        title: "Performance by Topic",
        subtitle: "Brand visibility per prompt topic across platforms",
        icon: "mdi-tag-multiple-outline",
        route: "Brands"
    },
    {
        type: "mentions_overview",
        group: "Mentions",
        label: "New / Lost Mentions",
        title: "New & Lost Mentions",
        subtitle: "How brand mentions changed between the start and the end of the period",
        icon: "mdi-swap-vertical",
        route: "Mentions"
    },
    {
        type: "sources_domains",
        group: "Sources",
        label: "Top Sources (Domains)",
        title: "Most Cited Sources",
        subtitle: "Domains AI platforms reference most often when answering the tracked prompts",
        icon: "mdi-web",
        route: "Sources",
        options: [{ key: "per_platform", label: "One table per platform" }]
    },
    {
        type: "sources_urls",
        group: "Sources",
        label: "Top Sources (URLs)",
        title: "Most Cited URLs",
        subtitle: "Individual pages AI platforms reference most often when answering the tracked prompts",
        icon: "mdi-link-variant",
        route: "Sources",
        options: [{ key: "per_platform", label: "One table per platform" }]
    }
]

// Brand modes for the trend chart. "top*" let the chart trim by visibility itself;
// "favorites" uses the project's favorites, "custom" the competitors picked in the builder.
export const TREND_BRAND_MODES = [
    { value: "top12", label: "Top 12 by visibility", max: 12 },
    { value: "top10", label: "Top 10 by visibility", max: 10 },
    { value: "top5", label: "Top 5 by visibility", max: 5 },
    { value: "favorites", label: "Favorites" },
    { value: "custom", label: "Select competitors" }
]

// Older reports only stored options.brands (custom picker) without a mode.
export function trendBrandMode(options) {
    const mode = options?.brand_mode
    if (TREND_BRAND_MODES.some((m) => m.value === mode)) return mode
    return Array.isArray(options?.brands) && options.brands.length ? "custom" : "top12"
}

// Only the named brands (case-insensitive canonicalName) — the project's own brand always stays in.
export function filterTrendBrands(brands, selected, ownBrand) {
    const list = Array.isArray(brands) ? brands : []
    const picked = new Set((Array.isArray(selected) ? selected : []).map((n) => (n || "").toLowerCase()).filter(Boolean))
    if (!picked.size) return list
    const own = (ownBrand || "").toLowerCase()
    return list.filter((b) => {
        const name = (b?.canonicalName || "").toLowerCase()
        return name && (picked.has(name) || name === own)
    })
}

// Brands + trim limit for the trend chart. maxBrands 0 = show every passed brand.
// Favorites/custom without any names fall back to the default top 12.
export function trendBrandScope(options, brands, favorites, ownBrand) {
    const list = Array.isArray(brands) ? brands : []
    const mode = trendBrandMode(options)
    const names = mode === "favorites" ? favorites : mode === "custom" ? options?.brands : null
    if (Array.isArray(names) && names.length) return { brands: filterTrendBrands(list, names, ownBrand), maxBrands: 0 }
    const top = TREND_BRAND_MODES.find((m) => m.value === mode && m.max)
    return { brands: list, maxBrands: top ? top.max : 12 }
}

export function sectionMeta(type) {
    return SECTION_REGISTRY.find((s) => s.type === type) || null
}

let _sid = 0
function sectionId() {
    return "s" + Date.now().toString(36) + (_sid++).toString(36)
}

// Build a self-contained section: an element (type) scoped to one platform and optional tag.
export function makeSection(type, platform = "all", tag = null, options = {}) {
    return { id: sectionId(), type, platform, tag: tag || null, enabled: true, order: 0, options }
}

// A fresh report starts with a small, sensible all-platforms deck the user then extends.
export function defaultSections() {
    return ["kpi_cards", "visibility_trend", "top_brands_per_platform", "brand_table"].map((type, i) => ({
        ...makeSection(type, "all", null),
        order: i
    }))
}

// Normalize saved sections: keep valid ones, ensure id/platform/tag/order, drop unknown types.
// Old reports (no per-section platform) default to "all" — the previous global-scope behavior.
export function normalizeSections(saved) {
    const list = Array.isArray(saved) ? saved.filter((s) => s && sectionMeta(s.type)) : []
    return list.map((s, i) => ({
        id: s.id || sectionId(),
        type: s.type,
        platform: s.platform || "all",
        tag: s.tag || null,
        enabled: s.enabled !== false,
        order: i,
        options: s.options || {}
    }))
}
